import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { buildMeta } from "~/lib/seo/meta";
import { JsonLd } from "~/components/seo/JsonLd";
import { serializeForServer } from "~/lib/server-serialization";
import { getLocale } from "~/paraglide/runtime";
import { LocalizedBlogLink } from "~/components/blog/BlogLink";
import { buildBlogArticleJsonLd, getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";

const fetchRolePost = createServerFn({ method: "GET" })
  .validator(z.string())
  .handler(async ({ data: slug }) => {
    const { getPostBySlug } = await import("~/lib/blog-queries");
    return serializeForServer(await getPostBySlug(slug, getLocale()));
  });

export const Route = createFileRoute("/blog/roles/$slug")({
  loader: ({ params }) => fetchRolePost({ data: params.slug }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const seoPage = {
      locale: loaderData.locale,
      pageType: loaderData.pageType,
      slug: loaderData.slug,
      countryCode: loaderData.countryCode,
    };
    return {
      meta: buildMeta({
        title: `${loaderData.title} | Alchema Blog`,
        description: loaderData.metaDescription ?? loaderData.excerpt ?? "",
        url: getBlogCanonical(seoPage).path,
      }),
      links: getBlogHeadLinks(seoPage, loaderData.alternate),
    };
  },
  component: RolePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center" style={{ backgroundColor: "var(--bg)" }}>
      <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>Role guide not found</h1>
      <LocalizedBlogLink href="/blog" className="mt-6 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--text-inverse)" }}>View all articles</LocalizedBlogLink>
    </div>
  ),
});

function RolePage() {
  const post = Route.useLoaderData();
  if (!post) throw notFound();

  const jsonLd = buildBlogArticleJsonLd(post);

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />
      <article className="mx-auto max-w-[680px] px-6 pb-24 pt-24">
        <nav aria-label="Breadcrumb" className="mb-8 text-[0.8125rem]" style={{ color: "var(--text-muted)" }}>
          <ol className="flex items-center gap-1.5">
            <li><LocalizedBlogLink href="/blog" className="transition-colors duration-150 hover:text-[var(--text)]">Blog</LocalizedBlogLink></li>
            <li aria-hidden="true" className="opacity-40">/</li>
            <li style={{ color: "var(--text-secondary)" }}>{post.title}</li>
          </ol>
        </nav>
        <h1 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold leading-[1.12] tracking-[-0.025em]" style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}>{post.title}</h1>
        {post.excerpt && (
          <div className="mt-8 rounded-[12px] p-5" style={{ background: "var(--surface-raised)" }}>
            <p className="text-[0.8125rem] font-medium uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Overview</p>
            <p className="text-[0.9375rem] leading-relaxed" style={{ color: "var(--text-secondary)", maxWidth: "60ch" }}>{post.excerpt}</p>
          </div>
        )}
        <hr className="my-10" style={{ borderColor: "var(--border-subtle)" }} />
        {post.content ? (
          <div className="blog-prose" dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p style={{ color: "var(--text-muted)" }}>Content is unavailable.</p>
        )}
        <div className="mt-12">
          <LocalizedBlogLink href="/blog" className="text-[0.8125rem] font-medium transition-colors duration-150 hover:text-[var(--accent)]" style={{ color: "var(--text-muted)" }}>&larr; All articles</LocalizedBlogLink>
        </div>
      </article>
    </div>
  );
}
